import { Injectable } from "@angular/core";
import { BaseHttpService } from "./base-http.service";
import { HttpClient } from "@angular/common/http";
import { JwtService } from "@core/services/jwt.service";
import { LoaderService } from "@core/services/loader.service";
import { BehaviorSubject, map, finalize, Observable } from "rxjs";
import { environment } from "@app/../environments/environment";
import { Customer } from "@core/models/customer.model";
import { RoleModel } from "@core/models/role.model";
import { PlatformModel } from "@core/models/platform.model";

@Injectable({
    providedIn: 'root'
  })
  export class CustomerService extends BaseHttpService {

    private customersSubject = new BehaviorSubject<Customer[]>([]);
    public customer$ = this.customersSubject.asObservable();

    private customersConnectedSubject = new BehaviorSubject<Customer | undefined>( undefined);
    public customersConnectedSubject$ = this.customersConnectedSubject.asObservable();


    private platformSubject = new BehaviorSubject<PlatformModel | undefined>(undefined);
    public platformSubject$ = this.platformSubject.asObservable();


    private roleSubject = new BehaviorSubject<RoleModel | undefined>(undefined);
    public role$ = this.roleSubject.asObservable();

    // Cache for customers
    private customersCache: Customer[] | null = null;
    private customersLoading: boolean = false;

    private readonly baseApiSpeedPresta = environment.baseApiSpeedpresta;
    private readonly customerUrl = environment.customerUrl;
    private readonly customerRoleUrl = environment.customerRoleUrl;
    private readonly customerWariflowUrl = environment.customerWariflowUrl;

    constructor(
        http: HttpClient,
        private loaderService: LoaderService,
        jwtService: JwtService
      ) {
        super(jwtService,http);
        if (jwtService.decodeToken()) {
            this.getAllCustomer();
            this.getRoleBYUser(jwtService.decodeToken()!.sub);
        }
      }


    getAllCustomer(forceRefresh: boolean = false): void {
        try {
            // Return cached data if available and not forcing refresh
            if (!forceRefresh && this.customersCache) {
                this.customersSubject.next(this.customersCache);
                return;
            }

            // Check if already loading to prevent duplicate requests
            if (this.customersLoading) {
                return;
            }

            // Mark as loading
            this.customersLoading = true;
            this.loaderService.startLoading();

            const userBody = {"status":"active","enterpriseId":this.getUserByToken()?.organization.toLocaleUpperCase(),"options":{"options.appId":"ORGANIZATION_CUSTOMERS"},"excluded":{}}
            this.retreiveAllFromKey(`${this.baseApiSpeedPresta}${this.getUserByToken()?.organization.toLocaleUpperCase()}/${this.customerUrl}`, userBody)
            .pipe(

                map(customers => customers.map(customers => ({
                    identifier: customers["options"]["identifier"],
                    photo: customers["options"]["photo"],
                    firstname: customers["options"]["firstname"],
                    surname: customers["options"]["surname"],
                    email: customers["options"]["email"],
                    phoneNumber: customers["options"]["mobile"],
                    siret: customers["options"]["siret"],
                    name: customers["options"]["name"],
                    address:  customers["options"]["address"],
                }))),
                finalize(() => {
                    this.loaderService.stopLoading();
                    this.customersLoading = false;
                })
            )
            .subscribe({
                next: (customers) => {
                    this.customersCache = customers as Customer[];
                    this.customersSubject.next(this.customersCache);
                },
                error: (error) => {
                    console.error('Error loading customers:', error);
                    this.customersLoading = false;
                    throw new Error("Erreur lors de la récupération des clients");
                }
            });
        } catch (error){
            throw new Error("Erreur lors de la récupération des clients")
        }

    }

    getCustomers(): Observable<Customer[]>{
        return this.customer$;
    }


    getRoleBYUser(email: string): void {
        this.loaderService.startLoading();
        const roleBody = {"enterpriseId":this.getUserByToken()?.organization.toLocaleUpperCase(),"options":{"options.account": email},"excluded":{"learning":""}}
        this.retreiveAllFromKey(this.baseApiSpeedPresta + this.getUserByToken()?.organization.toLocaleUpperCase() + '/' + this.customerRoleUrl, roleBody)
        .pipe(
            map(role => role.map((role: any ) => ({
                id: role["options"]["id"],
                label: role["options"]["label"],
                account: role["options"]["account"],
            }))),
            finalize(() => this.loaderService.stopLoading())
        )
        .subscribe((role: any) => this.roleSubject.next(role[0] as RoleModel))

    }

    getActualRole(): Observable<RoleModel | undefined> {
        return this.role$;
    }

    getCustomerConnected(email: string): void {
        this.loaderService.startLoading();
        const userBody = {"status":"active","enterpriseId":this.getUserByToken()?.organization.toLocaleUpperCase(),"options":{"options.email":email},"excluded":{"learning":""}}
        this.retreiveFromKey(this.baseApiSpeedPresta + this.getUserByToken()?.organization.toLocaleUpperCase() + '/' + this.customerWariflowUrl, userBody)
        .pipe(
            map(customers => ({
                identifier: customers["options"]["identifier"],
                photo: customers["options"]["photo"],
                firstname: customers["options"]["firstname"],
                surname: customers["options"]["surname"],
                email: customers["options"]["email"],
                phoneNumber: customers["options"]["mobile"],
            })),
            finalize(() => this.loaderService.stopLoading())
        )
        .subscribe(customer => this.customersConnectedSubject.next(customer as Customer));
    }

    /**
     * Clear customers cache
     * Useful when customers are updated and need to be refreshed
     */
    clearCustomersCache(): void {
        this.customersCache = null;
        this.customersLoading = false;
    }
}